/**
 * MS-014: a message photo, loaded through the signed-in API and shown from
 * memory. There is never a URL to forward, and the object URL is released when
 * the thumbnail goes. A HEIC the browser cannot draw is offered as a download.
 */

import { useEffect, useState } from 'react';

export function PhotoThumb({ load, mime, label, size = 96 }: { load: () => Promise<Blob>; mime: string; label: string; size?: number }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [undrawable, setUndrawable] = useState(false);

  useEffect(() => {
    let live = true;
    let made: string | null = null;
    load()
      .then((blob) => {
        if (!live) return;
        made = URL.createObjectURL(blob);
        setUrl(made);
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
    // The loader is a fresh closure on every render; the photo it points at does not change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const box = { width: size, height: size };
  if (failed) return <span className="grid place-items-center rounded-[10px] border text-[11px] text-ink-muted" style={box}>Photo unavailable</span>;
  if (!url) return <span className="block animate-pulse rounded-[10px] bg-ink/5" style={box} aria-label="Loading photo" />;
  if (undrawable || mime === 'image/heic') {
    return (
      <a href={url} download="photo.heic" className="grid place-items-center rounded-[10px] border px-2 text-center text-[11.5px] font-semibold underline" style={box}>
        HEIC photo, download
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-[10px] border" style={box} title={label}>
      <img src={url} alt={label} className="h-full w-full object-cover" onError={() => setUndrawable(true)} />
    </a>
  );
}
